import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFarmContext } from 'src/Elements/Farm'
import { useActiveInstallationContext } from 'src/Libs/installations'
import { useTypedSelector } from 'src/State/RootReducer'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { useUserRoles } from 'src/Hooks/user'
import { resolveInstallationStorageMismatch } from '../utils/resolve-installation-storage-mismatch'

interface InstallationAndStorageMismatchStatus {
  isChecking: boolean
  isMismatch: boolean
  logValue: string
}

export const useInstallationAndStorageMismatchStatus = (): InstallationAndStorageMismatchStatus => {
  const { isFarm, isError: isFarmError, farmMaster, farmClients, isLoading } = useFarmContext()
  const { installation, deviceLinks, storageLoading, installationLoading, installationErrorStatus, storageErrorStatus } =
    useActiveInstallationContext()
  const { isServiceUser } = useUserRoles()
  const installationStorageId = installation?.emsMasterDeviceId
  const installationStorageDeviceId = installation?.emsMasterId
  const storageDeviceLink = deviceLinks.find(deviceLink => deviceLink.id === installationStorageDeviceId)
  const selectedStorageIdFromStore = useTypedSelector(state => state.storage.activeStorage)
  const selectedStorageId =
    selectedStorageIdFromStore === 'undefined' || selectedStorageIdFromStore === 'null' ? undefined : selectedStorageIdFromStore

  const status = resolveInstallationStorageMismatch({
    isForbiddenRequest: installationErrorStatus === StatusCodes.FORBIDDEN || storageErrorStatus === StatusCodes.FORBIDDEN,
    isDataLoading: storageLoading || installationLoading || isLoading,
    selectedStorageId,
    isServiceUser,
    installation,
    storageDeviceLink,
    farm: {
      isFarm,
      isError: isFarmError,
      masterStorageId: farmMaster?.storageId,
      clientStorageIds: farmClients?.map(client => client.storageId) ?? [],
    },
  })

  return {
    ...status,
    logValue: `useBlockNavigationForInstallationAndStorageIdMismatch. installationStorageId: ${installationStorageId}, selectedStorageId: ${selectedStorageId}, isFarm: ${isFarm}`,
  }
}

/**
 * This hook blocks the navigation if the selected storageId does not belong to the selected installation.
 * It instead navigates to the not-found page.
 */
const useBlockNavigationForInstallationAndStorageIdMismatch = (): void => {
  const navigate = useNavigate()
  const { pageActions } = useNewRelic()
  const { isMismatch, logValue } = useInstallationAndStorageMismatchStatus()

  const isMismatchInstallationAndStorageId = useCallback(() => {
    return isMismatch
  }, [isMismatch])

  useEffect(() => {
    if (isMismatchInstallationAndStorageId()) {
      pageActions.logEvent({
        logValue,
      })

      navigate('/not-found', {
        state: {
          titleKey: 'errors.pageNotFound.mismatch-installation-storage.title',
          explanationKey: 'errors.pageNotFound.mismatch-installation-storage.explanation',
          hasAction: false,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMismatchInstallationAndStorageId])
}

export default useBlockNavigationForInstallationAndStorageIdMismatch
